<template>
  <div class="player-container">
    <Component
      :is="driverComponent"
      :data="video.player_data"
      @videoStarted="videoStartedHandler"
      @videoEnded="videoEndedHandler"
      @videoAppend="videoAppendHandler"
      @videoRemove="videoRemoveHandler"
      @videoPriority="videoPriorityHandler"
      @videoMute="videoMuteHandler"
      :poster="movie.public_thumbnail_image_path['large']"
      :title="movie.name"
      :subtitle="movie.description"
      :ref="(ref: any) => playerRef = ref"
      :index="index"
    />
    <div class="overlay" :class="{ visible: showOverlay }">
      <slot name="overlay" />
    </div>

    <div
      class="vjs-volume"
      @click="videoMuteHandler()"
      v-if="showVolumeDesktop"
    >
      <i
        class="fab fah fa-lg fas"
        :class="{
          'fa-volume-mute': muted,
          'fa-volume-up': !muted,
        }"
        aria-hidden="true"
      ></i>
    </div>

    <div
      class="vjs-volume-mobile"
      v-if="showVolumeMobile && (propagateStartShowing || propagateEndShowing)"
    >
      <i
        class="fab fah fa-lg fas"
        :class="{
          'fa-volume-mute': muted,
          'fa-volume-up': !muted,
          'anim-start': propagateStartShowing,
          'anim-end': propagateEndShowing,
        }"
        aria-hidden="true"
      ></i>
    </div>
  </div>
</template>

<script setup lang="ts">
import _NuxtApp from "@/types/NuxtApp";
import UserVideoConfig from "~/modules/video/UserConfig";

import Chunked from "./Player/Chunked.vue";

const { $EventBus } = <_NuxtApp>useNuxtApp();

const FULL_ANIMATION_DURATION = 1; // In seconds
const showVolumeDesktop = UserVideoConfig.isDesktop;
const showVolumeMobile = UserVideoConfig.isMobile;
const muted = UserVideoConfig.mutedRef;
let timeout: NodeJS.Timeout | null = null;

const propagateStartShowing = ref(false);
const propagateEndShowing = ref(false);
const playerRef = ref<typeof Chunked | null>(null);

interface _Props {
  video: any;
  showOverlay: boolean;
  movie: any;
  index: number;
}

const { video, movie, showOverlay, index } = withDefaults(defineProps<_Props>(), {
  showOverlay: false,
});

let ElementHandler = {
  videoAppendHandler: (movie: any, handler: any) => {},
  videoRemoveHandler: (movie: any) => {},
  videoHardAppendHandler: (movie: any, handler: any) => {},
};

const driverComponent = computed(() => {
  if (
    ["pro_chunked", "trailer_chunked", "free_chunked", "cloudflare"].includes(
      video.encode_driver
    )
  ) {
    return resolveComponent("VideoPlayerChunked");
  }

  return resolveComponent("VideoPlayerError");
});

function videoEndedHandler() {
  $EventBus.$emit("videoEnded", video);
}

function videoStartedHandler() {
  $EventBus.$emit("videoStarted", video);
}

function videoAppendHandler(handler: any) {
  ElementHandler.videoAppendHandler(movie, handler);
}

function videoRemoveHandler() {
  ElementHandler.videoRemoveHandler(movie);
}

function videoPriorityHandler(handler: any) {
  ElementHandler.videoHardAppendHandler(movie, handler);
}

function videoMuteHandler() {
  UserVideoConfig.toggleMuted();
  playerRef.value?._handler("mute", UserVideoConfig.muted);

  // Start animation block
  if (timeout) {
    clearTimeout(timeout);
    timeout = null;
    propagateStartShowing.value = false;
    propagateEndShowing.value = false;
  }

  propagateStartShowing.value = true;
  timeout = setTimeout(() => {
    propagateStartShowing.value = false;
    propagateEndShowing.value = true;

    timeout = setTimeout(() => {
      propagateEndShowing.value = false;
    }, (FULL_ANIMATION_DURATION / 2) * 1000);
  }, (FULL_ANIMATION_DURATION / 2) * 1000);
  // End animation block
}

watch(
  () => driverComponent,
  ({ value: val }) => {
    if (val === "VideoPlayerError") {
      console.error(`No se reconoce el player: ${video.encode_driver}`);
    }
  }
);

onBeforeUnmount(() => {
  if (timeout) {
    clearTimeout(timeout);
  }
});

onMounted(() => {
  const instance = getCurrentInstance();
  if (instance) {
    try {
      // @ts-ignore
      ElementHandler = instance.parent.parent.parent.parent.exposed.getVideoElementHandler();   // This is a hack to get the parent component
    } catch (error) {
      console.log("No se pudo obtener el manejador de video", error);
    }
  }
})
</script>

<style scoped lang="scss">
.player-wrapper {
  width: 100%;
  .player {
    width: 100%;
    height: 500px;
  }
}

.player-container {
  position: relative;

  .corner {
    position: absolute;
    z-index: 10;
    top: 0;
    right: 0;
    width: auto;
    height: auto;
  }

  .overlay {
    display: none;
    color: #ffffff;
    z-index: 9;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;

    &.visible {
      display: block;
    }
  }
}

.vjs-volume,
.vjs-volume-mobile {
  position: absolute;
  bottom: 1em;
  right: 1em;
  background-color: white;
  width: 2em;
  height: 2em;
  display: flex;
  justify-content: center;
  border-radius: 100%;
  cursor: pointer;

  i {
    font-size: 1em;
    color: black;
    vertical-align: middle;
    text-align: center;
    margin-block: auto;
  }
}

.vjs-volume-mobile {
  bottom: calc(50% - 2em / 2);
  right: calc(50% - 2em / 2);
  background-color: unset;
  cursor: unset;

  i {
    font-size: 2.5em;
    color: white;
    text-shadow: 0px 0px 5px black;

    animation-duration: 0.5s;

    &.anim-start {
      animation-name: appear;
    }

    &.anim-end {
      animation-name: disappear;
    }
  }
}

@keyframes appear {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes disappear {
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
  }
}
</style>
<style lang="scss">
.vjs-menu-button-popup.active {
  .vjs-menu {
    display: block;
  }
}
</style>
